import { useState } from "react";
import { UMBRAL_PARECIDO, VERSION_AVISO_ROSTRO } from "@huayruro/shared";
import { descargarCsv, mutar, useApi } from "../../lib/useApi";
import { ymdLima } from "../../lib/fecha-ui";
import type { PresenciaRostro, RegistroRostro } from "../../lib/rostro";
import type { SesionActiva } from "../../lib/tipos";
import { Button, Card, Chip, EmptyState, SectionLabel, useToast } from "../../components/ui";

// ============================================================
// Panel › Clientes › Reconocimiento (Kevin, 19-oct-2026: «una sección en el administrador que me ayude a
// administrar la información y a saber que cliente entra a la botica»; D-C7 de la sección 18 del plan del ojo).
// Dueño y Encargado: quién entró (lo que la webcam sugirió y quien atiende confirmó), quién tiene la cara
// registrada, revocar y borrar, y exportar los consentimientos. El vendedor no entra aquí: solo reconoce y registra.
// Ninguna foto vive en el sistema: solo huellas (números) que no se pueden volver a convertir en una cara.
// ============================================================

type Sucursal = { id: string; nombre: string };

const hora = (iso: string) => new Date(iso).toLocaleTimeString("es-PE", { hour: "2-digit", minute: "2-digit", timeZone: "America/Lima" });
const dia = (iso: string) => new Date(iso).toLocaleDateString("es-PE", { day: "2-digit", month: "short", year: "numeric", timeZone: "America/Lima" });
const ESTADO: Record<PresenciaRostro["estado"], { texto: string; tono: "ok" | "neutral" | "warn" }> = {
  confirmada: { texto: "Entró (confirmado)", tono: "ok" },
  sugerida: { texto: "Sin responder", tono: "neutral" },
  rechazada: { texto: "No era", tono: "warn" },
};

export function Reconocimiento({ sesion }: { sesion: SesionActiva }) {
  const esSuper = sesion.usuario.rol === "super_admin";
  const [sucSel, setSucSel] = useState("");
  const sucursales = useApi<{ sucursales: Sucursal[] }>(esSuper ? "/sucursales" : null);
  const suc = esSuper ? sucSel : (sesion.usuario.sucursalId ?? "");
  const q = esSuper && suc ? `sucursal_id=${suc}&` : "";
  const listo = !esSuper || !!suc;
  const [fecha, setFecha] = useState(() => ymdLima(new Date().toISOString()));
  const presencias = useApi<{ presencias: PresenciaRostro[] }>(listo ? `/clientes/rostro/presencias?${q}fecha=${fecha}` : null, [suc, fecha], { cadaMs: 30_000 });
  const registros = useApi<{ registros: RegistroRostro[] }>(listo ? `/clientes/rostro/registros${q ? `?${q.slice(0, -1)}` : ""}` : null, [suc]);
  const [confirmando, setConfirmando] = useState<string | null>(null);
  const toast = useToast();

  const entraron = (presencias.data?.presencias ?? []).filter((p) => p.estado === "confirmada");
  const filas = presencias.data?.presencias ?? [];
  const conCara = registros.data?.registros ?? [];

  async function revocar(r: RegistroRostro) {
    try {
      await mutar(`/clientes/${r.cliente_id}/rostro${q ? `?${q.slice(0, -1)}` : ""}`, { method: "DELETE" });
      toast(`Se borró la huella de ${r.nombre}. El permiso queda como retirado.`);
      setConfirmando(null);
      registros.recargar();
    } catch (e) {
      toast(e instanceof Error ? e.message : "No se pudo borrar");
    }
  }

  return (
    <div className="flex max-w-[1080px] flex-col gap-4" data-testid="reconocimiento">
      <p className="text-[13px] leading-relaxed text-ink-2">
        La webcam del mostrador reconoce a los clientes que <strong>aceptaron en la pantalla</strong>. El sistema solo sugiere
        («¿Es Rosa?») y quien atiende confirma. No se guarda ninguna foto: solo una huella hecha de números.
      </p>

      {esSuper && (
        <label className="flex items-center gap-2 text-[13px] text-ink-2">
          Botica
          <select value={sucSel} onChange={(e) => setSucSel(e.target.value)} className="min-h-[40px] rounded-[8px] border border-line-input bg-card px-2 text-[13px] text-ink">
            <option value="">Elige una botica…</option>
            {(sucursales.data?.sucursales ?? []).map((s) => (
              <option key={s.id} value={s.id}>
                {s.nombre}
              </option>
            ))}
          </select>
        </label>
      )}

      {!listo ? (
        <EmptyState title="Elige una botica" subtitle="Cada botica tiene su webcam y su lista de clientes." />
      ) : (
        <>
          <Card className="gap-3">
            <SectionLabel
              right={
                <input
                  type="date"
                  value={fecha}
                  onChange={(e) => setFecha(e.target.value)}
                  aria-label="Día"
                  className="min-h-[36px] rounded-[8px] border border-line-input bg-card px-2 text-[12.5px] text-ink"
                />
              }
            >
              Quién entró · {entraron.length} {entraron.length === 1 ? "cliente" : "clientes"}
            </SectionLabel>
            {presencias.cargando ? (
              <p className="py-3 text-center text-[13px] text-ink-3">Cargando…</p>
            ) : filas.length === 0 ? (
              <EmptyState title="Nadie reconocido este día" subtitle="Cuando la webcam reconozca a alguien y quien atiende diga «Sí», sale aquí." />
            ) : (
              <ul className="flex flex-col divide-y divide-line-row" data-testid="presencias">
                {filas.map((p) => (
                  <li key={p.id} className="flex flex-wrap items-center justify-between gap-2 py-2.5">
                    <div className="min-w-0">
                      <p className="text-[13.5px] font-semibold text-ink">
                        <span className="tabular-nums text-ink-2">{hora(p.visto_en)}</span> · {p.nombre}
                        {p.botica_cliente !== p.botica_vista && <span className="font-normal text-ink-3"> (cliente de {p.botica_cliente})</span>}
                      </p>
                      <p className="text-[12px] text-ink-3">
                        parecido {p.parecido.toFixed(2)}
                        {p.respondido_por ? ` · respondió ${p.respondido_por}` : ""}
                      </p>
                    </div>
                    <Chip variant={ESTADO[p.estado].tono}>{ESTADO[p.estado].texto}</Chip>
                  </li>
                ))}
              </ul>
            )}
          </Card>

          <Card className="gap-3">
            <SectionLabel
              right={
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => void descargarCsv(`/clientes/rostro/consentimientos.csv${q ? `?${q.slice(0, -1)}` : ""}`, `consentimientos-cara-${fecha}.csv`).catch(() => toast("No se pudo descargar"))}
                >
                  Exportar permisos
                </Button>
              }
            >
              Clientes con cara registrada · {conCara.length}
            </SectionLabel>
            {registros.cargando ? (
              <p className="py-3 text-center text-[13px] text-ink-3">Cargando…</p>
            ) : conCara.length === 0 ? (
              <EmptyState title="Nadie registrado todavía" subtitle="Se registra desde el Mostrador: ficha del cliente › «Registrar cara», con el «Acepto» del cliente." />
            ) : (
              <ul className="flex flex-col divide-y divide-line-row" data-testid="con-cara">
                {conCara.map((r) => (
                  <li key={r.consentimiento_id} className="flex flex-wrap items-center justify-between gap-2 py-2.5">
                    <div className="min-w-0">
                      <p className="text-[13.5px] font-semibold text-ink">{r.nombre}</p>
                      <p className="text-[12px] text-ink-3">
                        aceptó el {dia(r.aceptado_en)} en {r.botica}
                        {r.operador ? ` · registró ${r.operador}` : ""} · {r.huellas} tomas · calidad {r.calidad.toFixed(2)}
                        {r.texto_version !== VERSION_AVISO_ROSTRO ? ` · aviso ${r.texto_version}` : ""}
                      </p>
                    </div>
                    {confirmando === r.cliente_id ? (
                      <div className="flex gap-2">
                        <Button size="sm" onClick={() => void revocar(r)}>
                          Sí, borrar
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => setConfirmando(null)}>
                          Cancelar
                        </Button>
                      </div>
                    ) : (
                      <Button variant="outline" size="sm" onClick={() => setConfirmando(r.cliente_id)}>
                        Retirar permiso y borrar
                      </Button>
                    )}
                  </li>
                ))}
              </ul>
            )}
            <p className="text-[12px] leading-relaxed text-ink-3">
              Retirar el permiso borra la huella al instante; el registro del permiso queda con su fecha de retiro (es la prueba
              de que se pidió y se cumplió). Parecido mínimo para sugerir: {UMBRAL_PARECIDO.toFixed(2)}.
            </p>
          </Card>
        </>
      )}
    </div>
  );
}
